import { Button, Checkbox } from '@mantine/core';
import { Icon, Link, useOidcContext, useTheme, variants } from '@uag/react-core';
import { DOKA_360_PRODUCT_URL } from '@uag/welcome-shared/src/utils/externalLinks';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';

import { DOKA_360_COMPLETE_REGISTRATION_URL } from '../../shared/externalLinks';

type CompleteDoka360RegistrationLocationState = {
    customerIdentifier: string;
    companyName: string;
};

export const CompleteDoka360RegistrationView = () => {
    const { t } = useTranslation();
    const { user } = useOidcContext();
    const language = (user?.locale ?? 'en').slice(0, 2);
    const { termsAndConditionsLink } = useTheme();
    const termsAndConditionsUrl = termsAndConditionsLink?.(language);
    const { state } = useLocation();
    const { customerIdentifier, companyName } = (state as CompleteDoka360RegistrationLocationState | null) ?? {};

    const [isInformationCorrect, setIsInformationCorrect] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const isValid = isInformationCorrect && acceptedTerms;

    if (!customerIdentifier || !companyName) {
        return <Navigate to="/requests/create" replace />;
    }

    const handleSubmit = () => {
        if (!isValid) {
            return;
        }

        const searchParams = new URLSearchParams({ customerIdentifier, companyName });
        window.location.href = `${DOKA_360_COMPLETE_REGISTRATION_URL}/existing?${searchParams.toString()}`;
    };

    return (
        <div className="gap-2xl flex flex-col">
            <div className="gap-lg page-content-wrapper flex flex-col">
                <h1 className="text-4xl">{t('getStartedDoka360')}</h1>
                <p>
                    <Trans
                        components={{
                            doka360Link: (
                                <Link href={DOKA_360_PRODUCT_URL} target="_blank" variant={variants.link.external} />
                            ),
                        }}
                        i18nKey="doka360.availableForExistingCustomer"
                    />
                </p>
                <Checkbox
                    checked={isInformationCorrect}
                    className="multiline-checkbox"
                    label={
                        <div className="whitespace-pre-wrap">
                            {t('doka360.confirmAuthorizedAcceptLabel')}
                            <span className="text-text-alert">*</span>
                        </div>
                    }
                    onChange={(event) => setIsInformationCorrect(event.currentTarget.checked)}
                />
                <Checkbox
                    checked={acceptedTerms}
                    className="multiline-checkbox"
                    label={
                        <div className="whitespace-pre-wrap">
                            <Trans
                                components={{
                                    termsAndConditionsLink: <Link href={termsAndConditionsUrl} target="_blank" />,
                                }}
                                i18nKey="doka360.acceptTermsAndConditionsLabel"
                            />
                            <span className="text-text-alert">*</span>
                        </div>
                    }
                    onChange={(event) => setAcceptedTerms(event.currentTarget.checked)}
                />
            </div>
            <Button
                disabled={!isValid}
                rightSection={<Icon className="ml-base" icon="send" />}
                fullWidth
                onClick={handleSubmit}
            >
                {t('doka360.completeRegistration')}
            </Button>
        </div>
    );
};
